import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useNavigate } from 'react-router-dom';
import { resolveVerification, verificationValidation } from "../../../../services/auth";

import { vestResolver } from "@hookform/resolvers/vest";
import { grey } from "@mui/material/colors";
import validators from "./validators";

import { useState } from "react";

import DashedBreakline from "../../../../components/Shared/DashedBreakline";
import Timer from "../../../../components/Shared/Timer";
import { useNotificationContext } from "../../../../contexts/NotificationContext";
import { setToken } from "../../../../utils/functions";

const retryTextStyles = {
  fontSize: 13,
  sx: {
    my: 'auto',
    color: 'primary',
    textAlign: 'right',
    textDecoration: 'underline',
    cursor: 'pointer'
  }
}

export default function VerificationForm({ verification, setVerification }: any) {
  const navigate = useNavigate();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      verification: "",
    },
    resolver: vestResolver(validators)
  });

  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const { notification, setNotification } = useNotificationContext();

  const { mutateAsync: mutateAsyncValidation, isLoading } = useMutation(verificationValidation);
  const { mutateAsync: mutateAsyncResolveVerification, isLoading: isResentLoading } = useMutation(resolveVerification);

  const [retry, setRetry] = useState<boolean | null>(null)
  const [sent, setSent] = useState<boolean>(true)

  async function handleResolveValidationSend(email: string) {
    const verify = await mutateAsyncResolveVerification(email)

    if (verify?.errors) {
      setErrorMessage(verify?.errors?.[0].message)
      return
    }

    if (verify?.data?.created) {
      setSent(true)
      setRetry(false)
      setNotification({ ...notification, message: "We've sent a verification token to your email address. Just check your inbox and follow the instructions to complete this step.", severity: 'success', open: true })
    }
  }

  async function handleVerification(data: any) {
    const payload = {
      ...data,
      email: verification // Add the email from props
    };
    
    const response = await mutateAsyncValidation(payload)

    if (response?.errors && response?.errors?.length > 0) {
      setErrorMessage(response?.errors?.[0]?.message)
      return
    }

    const { access_token, uuid, exp } = response?.data || {};

    if (access_token && exp && uuid) {
      setToken(access_token, exp, uuid)
      navigate("/")
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit(handleVerification)}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 4, fontSize: 14 }}>{errorMessage}</Alert>
      )}
      <Controller
        name="verification"
        defaultValue=""
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            type="number"
            label="Verification Code (*)"
            error={!!errors?.verification}
            InputLabelProps={{ shrink: true }}
            helperText={errors?.verification?.message}
            sx={{ minWidth: "100%", "& input": { fontSize: 13 } }}
          />
        )}
      />
      <Stack direction="row" justifyContent="end">
        {isResentLoading && <CircularProgress size={14} sx={{ my: 'auto', mr: 0.5 }} />}
        <Typography {...retryTextStyles} onClick={() => verification && sent === false && (retry === null || retry) && handleResolveValidationSend(verification)}>
          {retry ? "Resend" : sent && <Timer initialMinute={1} callback={() => [setRetry(true), setSent(false)]} /> || "Resend"}
        </Typography>
      </Stack>

      <Button
        disabled={isLoading}
        type="submit"
        sx={{ mt: 3 }}
        fullWidth
        variant="contained"
      >
        {isLoading ? (
          <CircularProgress size={24} sx={{ color: grey[900] }} />
        ) : (
          "Verify"
        )}
      </Button>

      <DashedBreakline text="Use another account?" />

      <Button component="span" fullWidth sx={{ bgcolor: grey[50] }} onClick={() => setVerification(null)}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>
          Customer Login
        </Typography>
      </Button>
    </Box>
  );
}
